import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./Sidebar.css";
import images from "../../utilities/images/images";

const isPathActive = (pathname, path) =>
  pathname === path || pathname.startsWith(`${path}/`);

const PRACTICE_PATHS = [
  "/practice",
  "/pyq",
  "/cps",
  "/exam",
  "/ots",
  "/exam_cracker",
  "/mock_test",
  "/bit_sat",
  "/create-adaptive-cps",
  "/homework",
  "/ncert",
  "/question_bank",
  "/subjects",
  "/cuet+board",
  "/Cps_subjectTopic",
  "/CpsSubjectSubTopic",
  "/Pyq_subjectTopic",
];

const isPracticePath = (pathname) =>
  PRACTICE_PATHS.some((path) => isPathActive(pathname, path));

const Sidebar = ({ variant = "layout" }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const history = useHistory();
  const { pathname } = useLocation();

  const isDrawer = variant === "drawer";
  const isStudyDashboard =
    isDrawer ||
    pathname === "/dashboard" ||
    pathname.startsWith("/dashboard/");
  const showLabels = !collapsed || mobileOpen;

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    const openDrawer = () => setMobileOpen(true);
    const closeDrawer = () => setMobileOpen(false);
    window.addEventListener("app-sidebar-open", openDrawer);
    window.addEventListener("app-sidebar-close", closeDrawer);
    return () => {
      window.removeEventListener("app-sidebar-open", openDrawer);
      window.removeEventListener("app-sidebar-close", closeDrawer);
    };
  }, []);

  useEffect(() => {
    if (!isDrawer) return undefined;
    document.body.classList.add("has-app-sidebar");
    return () => {
      document.body.classList.remove("has-app-sidebar");
      document.body.classList.remove("has-app-sidebar--collapsed");
    };
  }, [isDrawer]);

  useEffect(() => {
    if (!isDrawer) return;
    document.body.classList.toggle("has-app-sidebar--collapsed", collapsed);
  }, [isDrawer, collapsed]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 769px)");
    const onChange = () => {
      if (media.matches) setMobileOpen(false);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  const goTo = (path) => {
    setMobileOpen(false);
    history.push(path);
  };

  return (
    <div className={isDrawer ? "sidebar-host sidebar-host--drawer" : "sidebar-host"}>
      <button
        type="button"
        className={`sidebar-mobile-toggle${
          mobileOpen ? " sidebar-mobile-toggle--hidden" : ""
        }`}
        aria-label="Open navigation menu"
        aria-expanded={mobileOpen}
        aria-controls="app-sidebar"
        onClick={() => setMobileOpen(true)}
      >
        <span className="sidebar-hamburger" aria-hidden="true">
          <span className="sidebar-hamburger-line" />
          <span className="sidebar-hamburger-line" />
          <span className="sidebar-hamburger-line" />
        </span>
      </button>

      {mobileOpen ? (
        <div
          className="sidebar-mobile-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      <aside
        id="app-sidebar"
        className={`sidebar${collapsed && !mobileOpen ? " collapsed" : ""}${
          mobileOpen ? " sidebar--mobile-open" : ""
        }${isDrawer ? " sidebar--drawer" : ""}`}
      >
        <div
          className={`sidebar-header ${
            collapsed && !mobileOpen ? "header-collapsed" : "header-open"
          }${isStudyDashboard ? " sidebar-header--study" : ""}`}
        >
          {showLabels &&
            (isStudyDashboard ? (
              <div className="sidebar-study-brand">
                <button
                  type="button"
                  className="sidebar-brand"
                  onClick={() => goTo("/dashboard")}
                  aria-label="Go to study dashboard"
                >
                  <img
                    src={images.motion_logo_new}
                    alt="Motion"
                    className="sidebar-brand-logo sidebar-brand-logo--study"
                  />
                </button>
                <p className="sidebar-study-space">STUDY SPACE</p>
              </div>
            ) : (
              <button
                type="button"
                className="sidebar-brand"
                onClick={() => goTo("/dashboard")}
                aria-label="Go to dashboard"
              >
                <img
                  src={images.motion_logo_new}
                  alt="Motion"
                  className="sidebar-brand-logo"
                />
              </button>
            ))}
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <img
              src={images.sidebar_collapse}
              alt=""
              className="menu-icon"
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            className="sidebar-mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation menu"
          >
            <span className="sidebar-mobile-close-icon" aria-hidden="true" />
          </button>
        </div>

        <nav className="sidebar-nav">
          <SidebarItem
            icon={images.homeImg}
            label="Dashboard"
            active={
              pathname === "/dashboard" ||
              pathname === "/user_dashboard" ||
              pathname.startsWith("/dashboard/")
            }
            collapsed={!showLabels}
            onClick={() => goTo("/dashboard")}
          />

          <SidebarItem
            icon={images.myspace_icon}
            iconClassName="sidebar-icon--dark-on-light"
            label="My Space"
            active={isPathActive(pathname, "/my-space")}
            collapsed={!showLabels}
            onClick={() => goTo("/my-space")}
          />

          <SidebarItem
            icon={images.model_training}
            label="Practice"
            active={isPracticePath(pathname)}
            collapsed={!showLabels}
            onClick={() => goTo("/practice")}
          />

          <SidebarItem
            icon={images.help_outline}
            label="Doubts"
            active={isPathActive(pathname, "/doubts")}
            collapsed={!showLabels}
            onClick={() => goTo("/doubts")}
          />

          <SidebarItem
            icon={images.storefront}
            label="Store"
            active={isPathActive(pathname, "/store")}
            collapsed={!showLabels}
            onClick={() => goTo("/store")}
          />
        </nav>
      </aside>
    </div>
  );
};

const SidebarItem = ({
  icon,
  iconClassName = "",
  label,
  active,
  collapsed,
  onClick,
}) => {
  return (
    <div
      className={`sidebar-item ${active ? "active" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <img
        src={icon}
        alt={label}
        className={["sidebar-icon", iconClassName].filter(Boolean).join(" ")}
      />
      {!collapsed && <span className="sidebar-label">{label}</span>}
    </div>
  );
};

export default Sidebar;
